import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import modelClassRequire from "@kaspernj/api-maker/build/model-class-require.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "name": {
      "column": {
        "default": null,
        "name": "name",
        "null": false,
        "type": "string"
      },
      "name": "name",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "Account",
  "collectionKey": "accounts",
  "collectionName": "accounts",
  "i18nKey": "account",
  "camelizedLower": "account",
  "name": "Account",
  "nameDasherized": "account",
  "pluralName": "accounts",
  "ransackable_associations": [
    {
      "className": "AccountSchool",
      "collectionName": "account_schools",
      "foreignKey": "account_id",
      "name": "account_schools",
      "macro": "has_many",
      "resource_name": "AccountSchool",
      "through": null
    },
    {
      "className": "AccountUser",
      "collectionName": "account_users",
      "foreignKey": "account_id",
      "name": "account_users",
      "macro": "has_many",
      "resource_name": "AccountUser",
      "through": null
    },
    {
      "className": "StripeSubscription",
      "collectionName": "stripe_subscriptions",
      "foreignKey": "stripe_customer_id",
      "name": "active_stripe_subscription",
      "macro": "has_one",
      "resource_name": "StripeSubscription",
      "through": "stripe_customer"
    },
    {
      "className": "Account::Audit",
      "collectionName": "account_audits",
      "foreignKey": "account_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "AccountAudit",
      "through": null
    },
    {
      "className": "Account::Audit",
      "collectionName": "account_audits",
      "foreignKey": "account_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "AccountAudit",
      "through": null
    },
    {
      "className": "School",
      "collectionName": "schools",
      "foreignKey": "account_id",
      "name": "schools",
      "macro": "has_many",
      "resource_name": "School",
      "through": null
    },
    {
      "className": "StripeCustomer",
      "collectionName": "stripe_customers",
      "foreignKey": "stripe_customer_id",
      "name": "stripe_customer",
      "macro": "belongs_to",
      "resource_name": "StripeCustomer",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "users",
      "macro": "has_many",
      "resource_name": "User",
      "through": "account_users"
    },
    {
      "className": "VatNumber",
      "collectionName": "vat_numbers",
      "foreignKey": "account_id",
      "name": "vat_numbers",
      "macro": "has_many",
      "resource_name": "VatNumber",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "name",
      "column": {
        "default": null,
        "name": "name",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "stripe_customer_id",
      "column": {
        "default": null,
        "name": "stripe_customer_id",
        "null": true,
        "type": null
      }
    },
    {
      "name": "subscriptions_count",
      "column": {
        "default": "0",
        "name": "subscriptions_count",
        "null": false,
        "type": "integer"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "AccountUser",
      "collectionName": "account_users",
      "foreignKey": "account_id",
      "name": "account_users",
      "macro": "has_many",
      "resource_name": "AccountUser",
      "through": null
    }
  ],
  "paramKey": "account",
  "primaryKey": "id"
}

/** Frontend model for Account. */
class Account extends BaseModel {
  /** @returns {Record<string, any>} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {string} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static createNew(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callCollectionCommand(
      {
        args,
        command: "create_new",
        collectionName: "accounts",
        type: "collection"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  addCreditCard(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "add_credit_card",
        primaryKey: this.primaryKey(),
        collectionName: "accounts",
        type: "member"
      },
      commandArgs
    ))
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./account-user.js").default>} */
  accountUsers() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const modelClass = modelClassRequire("AccountUser")

    const ransack = {}

    ransack["account_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "account_users",
        model: this,
        modelName: "AccountUser",
        modelClass
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./account-user.js").default>>} */
  loadAccountUsers() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const modelClass = modelClassRequire("AccountUser")

    const ransack = {}

    ransack["account_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "account_users",
        model: this,
        modelClass
      },
      {ransack}
    )
  }
}

export default Account
